import {
    FaAppleAlt, FaBook, FaBriefcase, FaShieldAlt, FaDatabase, FaBuilding,
    FaCalendarPlus, FaLeaf, FaShoppingBag, FaTools, FaLandmark, FaRocket, FaUserTie, FaChartLine,
    FaCalendarAlt, FaCloud, FaCode, FaCogs, FaCommentDollar, FaFilm, FaGamepad, FaHandshake, FaHeart, FaLaptopCode, FaLightbulb, FaMobileAlt, FaPaintRoller, FaRoute, FaRunning, FaUserAstronaut, FaUsers, FaWeightHanging
} from 'react-icons/fa';

// =============================================================================
// AI PROVIDERS - Scalable Provider System
// =============================================================================

export const AI_PROVIDERS = {
    openai: {
        id: 'openai',
        name: 'OpenAI',
        baseUrl: 'https://api.openai.com/v1',
    },
    anthropic: {
        id: 'anthropic',
        name: 'Anthropic',
        baseUrl: 'https://api.anthropic.com',
    },
    google: {
        id: 'google',
        name: 'Google AI',
        baseUrl: 'https://generativelanguage.googleapis.com/v1beta',
    },
    xai: {
        id: 'xai',
        name: 'xAI',
        baseUrl: 'https://api.x.ai/v1',
    },
    deepseek: {
        id: 'deepseek',
        name: 'DeepSeek',
        baseUrl: 'https://api.deepseek.com/v1',
    },
    glm: {
        id: 'glm',
        name: 'Z.AI (GLM)',
        baseUrl: 'https://api.z.ai/api/paas/v4',
    },
    kimi: {
        id: 'kimi',
        name: 'Moonshot (Kimi)',
        baseUrl: 'https://api.moonshot.ai/v1',
    },
    qwen: {
        id: 'qwen',
        // Alibaba Model Studio / DashScope, OpenAI-compatible endpoint.
        // This is the international host; mainland China uses dashscope.aliyuncs.com.
        name: 'Alibaba (Qwen)',
        baseUrl: 'https://dashscope-intl.aliyuncs.com/compatible-mode/v1',
    },
} as const;

export type AIProvider = keyof typeof AI_PROVIDERS;

// =============================================================================
// MODEL CAPABILITIES - What each model can do
// =============================================================================

export type ModelCapability =
    | 'chat'
    | 'vision'
    | 'reasoning'
    | 'code'
    | 'function_calling'
    | 'json_mode'
    | 'streaming'
    | 'extended_thinking';

/**
 * How a model's reasoning/thinking is requested.
 *
 * - 'none'     — model does not think; send no thinking options.
 * - 'budget'   — legacy explicit thinking budget. Anthropic Claude 3.7–4.6 only:
 *                `thinking: { type: 'enabled', budgetTokens: N }`.
 * - 'adaptive' — the model decides how much to think. The thinking parameter must
 *                be OMITTED entirely (adaptive is the server-side default) and depth
 *                is steered with `effort`. Required for Claude Opus 5 / Sonnet 5 /
 *                Fable 5 / Opus 4.7 / 4.8, where sending `budgetTokens` returns a 400.
 * - 'effort'   — reasoning depth is controlled purely by an effort/level parameter
 *                (OpenAI `reasoningEffort`, xAI/DeepSeek equivalents).
 */
export type ThinkingMode = 'none' | 'budget' | 'adaptive' | 'effort';

/** Reasoning effort levels. Note: @ai-sdk/anthropic only accepts low|medium|high. */
export type ReasoningEffort = 'low' | 'medium' | 'high' | 'xhigh';

// =============================================================================
// MODEL CONFIGURATION - Single source of truth for all models
// =============================================================================

export interface ModelConfig {
    id: string;                    // API model ID (e.g., 'gpt-5.6-sol')
    provider: AIProvider;          // Provider key
    displayName: string;           // Human-readable name
    description: string;           // Short description
    contextWindow: number;         // Max input tokens
    maxOutputTokens: number;       // Max output tokens
    capabilities: readonly ModelCapability[];
    isReasoning?: boolean;         // For reasoning/thinking models
    isDeprecated?: boolean;        // Hidden from pickers, but still resolvable
    releaseDate?: string;          // When the model was released
    knowledgeCutoff?: string;      // Training data cutoff, when published
    tier: 'flagship' | 'standard' | 'fast' | 'legacy';

    /**
     * Whether the model accepts sampling parameters (temperature/top_p/top_k).
     * Defaults to true when omitted. Set to false for models that reject them
     * with a 400 — Claude Opus 5 / Sonnet 5 / Fable 5 / Opus 4.7 / 4.8, and the
     * OpenAI reasoning models. Consumed by `supportsSampling()`.
     */
    supportsSampling?: boolean;

    /** How to request thinking for this model. Defaults to 'none'. */
    thinking?: ThinkingMode;

    /** Default reasoning effort to request when the model supports it. */
    defaultEffort?: ReasoningEffort;
}

// =============================================================================
// MODEL REGISTRY - All supported models
// =============================================================================

export const MODEL_REGISTRY = {
    // =========================================================================
    // OPENAI - GPT Models
    // =========================================================================

    // ── GPT-5.6 family (current — GA July 9, 2026) ────────────────────────────
    // All three share a 1.05M context window, 128K max output, and configurable
    // reasoning effort (none | low | medium | high | xhigh | max).
    'gpt-5.6-sol': {
        id: 'gpt-5.6-sol',
        provider: 'openai',
        displayName: 'GPT-5.6 Sol',
        description: 'Frontier model for complex professional, coding and research work',
        contextWindow: 1050000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'effort',
        defaultEffort: 'high',
        tier: 'flagship',
        releaseDate: '2026-07',
        knowledgeCutoff: '2026-02-16',
    },
    'gpt-5.6-terra': {
        id: 'gpt-5.6-terra',
        provider: 'openai',
        displayName: 'GPT-5.6 Terra',
        description: 'Balances intelligence and cost for everyday production work',
        contextWindow: 1050000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'effort',
        defaultEffort: 'medium',
        tier: 'flagship',
        releaseDate: '2026-07',
        knowledgeCutoff: '2026-02-16',
    },
    'gpt-5.6-luna': {
        id: 'gpt-5.6-luna',
        provider: 'openai',
        displayName: 'GPT-5.6 Luna',
        description: 'Fastest and cheapest GPT-5.6 for high-volume workloads',
        contextWindow: 1050000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'effort',
        defaultEffort: 'low',
        tier: 'fast',
        releaseDate: '2026-07',
        knowledgeCutoff: '2026-02-16',
    },

    // ── GPT-5.x (previous generation) ─────────────────────────────────────────
    'gpt-5.2': {
        id: 'gpt-5.2',
        provider: 'openai',
        displayName: 'GPT-5.2',
        description: 'Previous-generation GPT-5 with 400K context and advanced reasoning',
        contextWindow: 400000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'effort',
        defaultEffort: 'medium',
        tier: 'standard',
        releaseDate: '2025-12',
    },

    // GPT-5.1 (November 2025)
    'gpt-5.1': {
        id: 'gpt-5.1',
        provider: 'openai',
        displayName: 'GPT-5.1',
        description: 'Refined GPT-5 with improved tone and efficiency',
        contextWindow: 400000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'effort',
        tier: 'standard',
        releaseDate: '2025-11',
    },

    // GPT-5 Base (August 2025)
    'gpt-5': {
        id: 'gpt-5',
        provider: 'openai',
        displayName: 'GPT-5',
        description: 'Major breakthrough in coding, math, and multi-step reasoning',
        contextWindow: 400000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'effort',
        tier: 'standard',
        releaseDate: '2025-08',
    },
    // Note: gpt-5 alias above always points to latest GPT-5 version

    // GPT-4.1 Series (Latest - April 2025)
    'gpt-4.1': {
        id: 'gpt-4.1',
        provider: 'openai',
        displayName: 'GPT-4.1',
        description: 'Major upgrade with 1M context, superior coding & instruction following',
        contextWindow: 1000000,
        maxOutputTokens: 32768,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'json_mode', 'streaming'],
        tier: 'flagship',
        releaseDate: '2025-04',
    },
    'gpt-4.1-mini': {
        id: 'gpt-4.1-mini',
        provider: 'openai',
        displayName: 'GPT-4.1 Mini',
        description: 'Fast and efficient with 1M context window',
        contextWindow: 1000000,
        maxOutputTokens: 32768,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'json_mode', 'streaming'],
        tier: 'fast',
        releaseDate: '2025-04',
    },
    'gpt-4.1-nano': {
        id: 'gpt-4.1-nano',
        provider: 'openai',
        displayName: 'GPT-4.1 Nano',
        description: 'Ultra-fast, most cost-effective model',
        contextWindow: 1000000,
        maxOutputTokens: 32768,
        capabilities: ['chat', 'code', 'function_calling', 'json_mode', 'streaming'],
        tier: 'fast',
        releaseDate: '2025-04',
    },

    // GPT-4.5 Preview (Deprecated)
    'gpt-4.5-preview': {
        id: 'gpt-4.5-preview',
        provider: 'openai',
        displayName: 'GPT-4.5 Preview',
        description: 'Advanced GPT model with enhanced reasoning',
        contextWindow: 128000,
        maxOutputTokens: 16384,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'json_mode', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-02',
    },

    // GPT-4o Series
    'gpt-4o': {
        id: 'gpt-4o',
        provider: 'openai',
        displayName: 'GPT-4o',
        description: 'Fast, intelligent, multimodal flagship model',
        contextWindow: 128000,
        maxOutputTokens: 16384,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'json_mode', 'streaming'],
        tier: 'flagship',
        releaseDate: '2024-05',
    },
    'gpt-4o-mini': {
        id: 'gpt-4o-mini',
        provider: 'openai',
        displayName: 'GPT-4o Mini',
        description: 'Fast and affordable small model',
        contextWindow: 128000,
        maxOutputTokens: 16384,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'json_mode', 'streaming'],
        tier: 'fast',
        releaseDate: '2024-07',
    },
    'chatgpt-4o-latest': {
        id: 'chatgpt-4o-latest',
        provider: 'openai',
        displayName: 'ChatGPT-4o Latest',
        description: 'Latest ChatGPT snapshot, optimized for chat',
        contextWindow: 128000,
        maxOutputTokens: 16384,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'json_mode', 'streaming'],
        tier: 'flagship',
        releaseDate: '2024-10',
    },

    // O-Series Reasoning Models
    'o3-pro': {
        id: 'o3-pro',
        provider: 'openai',
        displayName: 'O3 Pro',
        description: 'Most powerful reasoning model with maximum compute',
        contextWindow: 200000,
        maxOutputTokens: 100000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'extended_thinking', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'effort',
        tier: 'standard',
        releaseDate: '2025-06',
    },
    'o3': {
        id: 'o3',
        provider: 'openai',
        displayName: 'O3',
        description: 'Advanced reasoning model for complex tasks',
        contextWindow: 200000,
        maxOutputTokens: 100000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'effort',
        tier: 'standard',
        releaseDate: '2025-04',
    },
    'o3-mini': {
        id: 'o3-mini',
        provider: 'openai',
        displayName: 'O3 Mini',
        description: 'Fast reasoning model, cost-effective',
        contextWindow: 200000,
        maxOutputTokens: 100000,
        capabilities: ['chat', 'reasoning', 'code', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'effort',
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-01',
    },
    'o4-mini': {
        id: 'o4-mini',
        provider: 'openai',
        displayName: 'O4 Mini',
        description: 'Fast, cost-effective reasoning model',
        contextWindow: 200000,
        maxOutputTokens: 100000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'effort',
        tier: 'fast',
        releaseDate: '2025-04',
    },
    'o1': {
        id: 'o1',
        provider: 'openai',
        displayName: 'O1',
        description: 'Superseded reasoning model',
        contextWindow: 200000,
        maxOutputTokens: 100000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'effort',
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2024-12',
    },
    'o1-mini': {
        id: 'o1-mini',
        provider: 'openai',
        displayName: 'O1 Mini',
        description: 'Superseded small reasoning model',
        contextWindow: 128000,
        maxOutputTokens: 65536,
        capabilities: ['chat', 'reasoning', 'code', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'effort',
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2024-09',
    },
    'o1-pro': {
        id: 'o1-pro',
        provider: 'openai',
        displayName: 'O1 Pro',
        description: 'Superseded high-compute reasoning model',
        contextWindow: 200000,
        maxOutputTokens: 100000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'extended_thinking'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'effort',
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2024-12',
    },
    'o1-preview': {
        id: 'o1-preview',
        provider: 'openai',
        displayName: 'O1 Preview',
        description: 'Early access reasoning model',
        contextWindow: 128000,
        maxOutputTokens: 32768,
        capabilities: ['chat', 'reasoning', 'code'],
        isReasoning: true,
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2024-09',
    },

    // GPT-4 Turbo
    'gpt-4-turbo': {
        id: 'gpt-4-turbo',
        provider: 'openai',
        displayName: 'GPT-4 Turbo',
        description: 'GPT-4 Turbo with vision',
        contextWindow: 128000,
        maxOutputTokens: 4096,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'json_mode', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2024-04',
    },

    // GPT-4 Base
    'gpt-4': {
        id: 'gpt-4',
        provider: 'openai',
        displayName: 'GPT-4',
        description: 'Original GPT-4 model',
        contextWindow: 8192,
        maxOutputTokens: 4096,
        capabilities: ['chat', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2023-03',
    },
    'gpt-4-32k': {
        id: 'gpt-4-32k',
        provider: 'openai',
        displayName: 'GPT-4 32K',
        description: 'GPT-4 with extended context',
        contextWindow: 32768,
        maxOutputTokens: 4096,
        capabilities: ['chat', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2023-03',
    },

    // GPT-3.5
    'gpt-3.5-turbo': {
        id: 'gpt-3.5-turbo',
        provider: 'openai',
        displayName: 'GPT-3.5 Turbo',
        description: 'Fast and efficient for simple tasks',
        contextWindow: 16385,
        maxOutputTokens: 4096,
        capabilities: ['chat', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2023-03',
    },

    // Legacy OpenAI models
    'gpt-4-1106-preview': {
        id: 'gpt-4-1106-preview',
        provider: 'openai',
        displayName: 'GPT-4 Turbo Preview (Nov 2023)',
        description: 'Legacy GPT-4 Turbo preview',
        contextWindow: 128000,
        maxOutputTokens: 4096,
        capabilities: ['chat', 'code', 'function_calling', 'json_mode', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2023-11',
    },
    'gpt-4-vision-preview': {
        id: 'gpt-4-vision-preview',
        provider: 'openai',
        displayName: 'GPT-4 Vision Preview',
        description: 'Legacy GPT-4 with vision',
        contextWindow: 128000,
        maxOutputTokens: 4096,
        capabilities: ['chat', 'vision', 'code', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2023-11',
    },
    'gpt-4-0613': {
        id: 'gpt-4-0613',
        provider: 'openai',
        displayName: 'GPT-4 (June 2023)',
        description: 'Legacy GPT-4 snapshot',
        contextWindow: 8192,
        maxOutputTokens: 4096,
        capabilities: ['chat', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2023-06',
    },
    'gpt-4-32k-0613': {
        id: 'gpt-4-32k-0613',
        provider: 'openai',
        displayName: 'GPT-4 32K (June 2023)',
        description: 'Legacy GPT-4 32K snapshot',
        contextWindow: 32768,
        maxOutputTokens: 4096,
        capabilities: ['chat', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2023-06',
    },
    'gpt-3.5-turbo-16k': {
        id: 'gpt-3.5-turbo-16k',
        provider: 'openai',
        displayName: 'GPT-3.5 Turbo 16K',
        description: 'Legacy GPT-3.5 with extended context',
        contextWindow: 16385,
        maxOutputTokens: 4096,
        capabilities: ['chat', 'code', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2023-06',
    },
    'gpt-3.5-turbo-1106': {
        id: 'gpt-3.5-turbo-1106',
        provider: 'openai',
        displayName: 'GPT-3.5 Turbo (Nov 2023)',
        description: 'Legacy GPT-3.5 snapshot',
        contextWindow: 16385,
        maxOutputTokens: 4096,
        capabilities: ['chat', 'code', 'json_mode', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2023-11',
    },
    'gpt-3.5-turbo-instruct': {
        id: 'gpt-3.5-turbo-instruct',
        provider: 'openai',
        displayName: 'GPT-3.5 Turbo Instruct',
        description: 'Legacy instruction-following model',
        contextWindow: 4096,
        maxOutputTokens: 4096,
        capabilities: ['chat'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2023-09',
    },
    'gpt-3.5-turbo-0301': {
        id: 'gpt-3.5-turbo-0301',
        provider: 'openai',
        displayName: 'GPT-3.5 Turbo (March 2023)',
        description: 'Legacy GPT-3.5 snapshot',
        contextWindow: 4096,
        maxOutputTokens: 4096,
        capabilities: ['chat', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2023-03',
    },
    'gpt-4-0314': {
        id: 'gpt-4-0314',
        provider: 'openai',
        displayName: 'GPT-4 (March 2023)',
        description: 'Legacy GPT-4 snapshot',
        contextWindow: 8192,
        maxOutputTokens: 4096,
        capabilities: ['chat', 'code', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2023-03',
    },
    'gpt-4-32k-0314': {
        id: 'gpt-4-32k-0314',
        provider: 'openai',
        displayName: 'GPT-4 32K (March 2023)',
        description: 'Legacy GPT-4 32K snapshot',
        contextWindow: 32768,
        maxOutputTokens: 4096,
        capabilities: ['chat', 'code', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2023-03',
    },

    // =========================================================================
    // ANTHROPIC - Claude Models
    // =========================================================================

    // ── Claude 5 family (current) ─────────────────────────────────────────────
    // IMPORTANT: these models REJECT sampling params (temperature/top_p/top_k)
    // and REJECT `thinking.budgetTokens` with a 400. They use adaptive thinking,
    // which means the thinking parameter must be omitted entirely — adaptive is
    // the server-side default. Depth is steered with `effort`.
    'claude-fable-5': {
        id: 'claude-fable-5',
        provider: 'anthropic',
        displayName: 'Claude Fable 5',
        description: 'Most capable Claude: hardest reasoning and long-horizon agentic work',
        contextWindow: 1000000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'adaptive',
        defaultEffort: 'high',
        tier: 'flagship',
        releaseDate: '2026-06',
    },
    'claude-opus-5': {
        id: 'claude-opus-5',
        provider: 'anthropic',
        displayName: 'Claude Opus 5',
        description: 'Complex agentic coding and enterprise work with 1M context',
        contextWindow: 1000000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'adaptive',
        defaultEffort: 'high',
        tier: 'flagship',
        releaseDate: '2026-05',
    },
    'claude-sonnet-5': {
        id: 'claude-sonnet-5',
        provider: 'anthropic',
        displayName: 'Claude Sonnet 5',
        description: 'Best speed-to-intelligence balance; near-Opus coding quality',
        contextWindow: 1000000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'adaptive',
        defaultEffort: 'high',
        tier: 'flagship',
        releaseDate: '2026-05',
    },

    // ── Claude Opus 4.7 / 4.8 (adaptive thinking, no sampling params) ─────────
    'claude-opus-4-8': {
        id: 'claude-opus-4-8',
        provider: 'anthropic',
        displayName: 'Claude Opus 4.8',
        description: 'Highly autonomous long-horizon agentic work and knowledge tasks',
        contextWindow: 1000000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'extended_thinking', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'adaptive',
        defaultEffort: 'high',
        tier: 'standard',
        releaseDate: '2026-04',
    },
    'claude-opus-4-7': {
        id: 'claude-opus-4-7',
        provider: 'anthropic',
        displayName: 'Claude Opus 4.7',
        description: 'Autonomous agentic work, vision and memory tasks',
        contextWindow: 1000000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'extended_thinking', 'streaming'],
        isReasoning: true,
        supportsSampling: false,
        thinking: 'adaptive',
        defaultEffort: 'high',
        tier: 'standard',
        releaseDate: '2026-03',
    },

    // ── Claude 4.6 (adaptive thinking; still accepts sampling params) ─────────
    'claude-opus-4-6': {
        id: 'claude-opus-4-6',
        provider: 'anthropic',
        displayName: 'Claude Opus 4.6',
        description: 'Frontier coding and agents with 1M context',
        contextWindow: 1000000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'extended_thinking', 'streaming'],
        isReasoning: true,
        thinking: 'adaptive',
        defaultEffort: 'high',
        tier: 'standard',
        releaseDate: '2026-02',
    },
    'claude-sonnet-4-6': {
        id: 'claude-sonnet-4-6',
        provider: 'anthropic',
        displayName: 'Claude Sonnet 4.6',
        description: 'Fast, cost-efficient Sonnet with adaptive thinking and 1M context',
        contextWindow: 1000000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'extended_thinking', 'streaming'],
        isReasoning: true,
        thinking: 'adaptive',
        defaultEffort: 'medium',
        tier: 'standard',
        releaseDate: '2026-01',
    },

    // ── Claude 4.5 series (budget-style thinking) ─────────────────────────────
    'claude-opus-4-5': {
        id: 'claude-opus-4-5',
        provider: 'anthropic',
        displayName: 'Claude Opus 4.5',
        description: 'Premium model combining maximum intelligence with practical performance',
        contextWindow: 200000,
        maxOutputTokens: 32000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'extended_thinking', 'streaming'],
        isReasoning: true,
        thinking: 'budget',
        tier: 'standard',
        releaseDate: '2025-11',
    },
    'claude-sonnet-4-5': {
        id: 'claude-sonnet-4-5',
        provider: 'anthropic',
        displayName: 'Claude Sonnet 4.5',
        description: 'Strong model for real-world agents and coding',
        contextWindow: 200000,
        maxOutputTokens: 16000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'extended_thinking', 'streaming'],
        isReasoning: true,
        thinking: 'budget',
        tier: 'standard',
        releaseDate: '2025-09',
    },
    'claude-haiku-4-5': {
        id: 'claude-haiku-4-5',
        provider: 'anthropic',
        displayName: 'Claude Haiku 4.5',
        description: 'Fastest and most cost-effective Claude for simple tasks',
        contextWindow: 200000,
        maxOutputTokens: 64000,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'extended_thinking', 'streaming'],
        isReasoning: true,
        thinking: 'budget',
        tier: 'fast',
        releaseDate: '2025-10',
    },

    // ── Retired Claude models (hidden from pickers, kept resolvable) ──────────
    // Every entry below has passed its Anthropic retirement date and will 404.
    'claude-opus-4-1': {
        id: 'claude-opus-4-1',
        provider: 'anthropic',
        displayName: 'Claude Opus 4.1',
        description: 'Retired 2026-08-05 — use Claude Opus 5',
        contextWindow: 200000,
        maxOutputTokens: 32000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'extended_thinking', 'streaming'],
        thinking: 'budget',
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-08',
    },
    'claude-opus-4-0': {
        id: 'claude-opus-4-0',
        provider: 'anthropic',
        displayName: 'Claude Opus 4',
        description: 'Retired 2026-06-15 — use Claude Opus 5',
        contextWindow: 200000,
        maxOutputTokens: 32000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'extended_thinking', 'streaming'],
        thinking: 'budget',
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-05',
    },
    'claude-sonnet-4-0': {
        id: 'claude-sonnet-4-0',
        provider: 'anthropic',
        displayName: 'Claude Sonnet 4',
        description: 'Retired 2026-06-15 — use Claude Sonnet 5',
        contextWindow: 200000,
        maxOutputTokens: 16000,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'extended_thinking', 'streaming'],
        thinking: 'budget',
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-05',
    },
    'claude-3-7-sonnet-latest': {
        id: 'claude-3-7-sonnet-latest',
        provider: 'anthropic',
        displayName: 'Claude 3.7 Sonnet',
        description: 'Retired 2026-02-19 — use Claude Sonnet 5',
        contextWindow: 200000,
        maxOutputTokens: 16000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'extended_thinking', 'streaming'],
        thinking: 'budget',
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-02',
    },
    'claude-3-5-sonnet-latest': {
        id: 'claude-3-5-sonnet-latest',
        provider: 'anthropic',
        displayName: 'Claude 3.5 Sonnet',
        description: 'Retired 2025-10-28 — use Claude Sonnet 5',
        contextWindow: 200000,
        maxOutputTokens: 8192,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2024-10',
    },
    'claude-3-5-haiku-latest': {
        id: 'claude-3-5-haiku-latest',
        provider: 'anthropic',
        displayName: 'Claude 3.5 Haiku',
        description: 'Retired 2026-02-19 — use Claude Haiku 4.5',
        contextWindow: 200000,
        maxOutputTokens: 8192,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2024-11',
    },
    'claude-3-opus-latest': {
        id: 'claude-3-opus-latest',
        provider: 'anthropic',
        displayName: 'Claude 3 Opus',
        description: 'Retired 2026-01-05 — use Claude Opus 5',
        contextWindow: 200000,
        maxOutputTokens: 4096,
        capabilities: ['chat', 'vision', 'code', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2024-02',
    },
    'claude-3-haiku-20240307': {
        id: 'claude-3-haiku-20240307',
        provider: 'anthropic',
        displayName: 'Claude 3 Haiku',
        description: 'Retired 2026-04-19 — use Claude Haiku 4.5',
        contextWindow: 200000,
        maxOutputTokens: 4096,
        capabilities: ['chat', 'vision', 'code', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2024-03',
    },

    // =========================================================================
    // GOOGLE - Gemini Models
    // =========================================================================

    // ── Gemini 3.x (current) ──────────────────────────────────────────────────
    // Whole family shares 1,048,576 input / 65,536 output token limits.
    'gemini-3.6-flash': {
        id: 'gemini-3.6-flash',
        provider: 'google',
        displayName: 'Gemini 3.6 Flash',
        description: 'Latest Gemini balancing speed and intelligence for agentic tasks',
        contextWindow: 1048576,
        maxOutputTokens: 65536,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'streaming'],
        isReasoning: true,
        thinking: 'adaptive',
        tier: 'flagship',
        releaseDate: '2026-07',
    },
    'gemini-3.1-pro-preview': {
        id: 'gemini-3.1-pro-preview',
        provider: 'google',
        displayName: 'Gemini 3.1 Pro',
        description: 'Advanced intelligence and agentic coding (preview)',
        contextWindow: 1048576,
        maxOutputTokens: 65536,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'streaming'],
        isReasoning: true,
        thinking: 'adaptive',
        tier: 'flagship',
        releaseDate: '2026-06',
    },
    'gemini-3.5-flash': {
        id: 'gemini-3.5-flash',
        provider: 'google',
        displayName: 'Gemini 3.5 Flash',
        description: 'Fast, capable workhorse model with reasoning',
        contextWindow: 1048576,
        maxOutputTokens: 65536,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'streaming'],
        isReasoning: true,
        thinking: 'adaptive',
        tier: 'standard',
        releaseDate: '2026-05',
    },
    'gemini-3.5-flash-lite': {
        id: 'gemini-3.5-flash-lite',
        provider: 'google',
        displayName: 'Gemini 3.5 Flash-Lite',
        description: 'Fastest, most cost-effective 3.5 model for high throughput',
        contextWindow: 1048576,
        maxOutputTokens: 65536,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'streaming'],
        isReasoning: true,
        thinking: 'adaptive',
        tier: 'fast',
        releaseDate: '2026-05',
    },
    'gemini-3.1-flash-lite': {
        id: 'gemini-3.1-flash-lite',
        provider: 'google',
        displayName: 'Gemini 3.1 Flash-Lite',
        description: 'Low-latency, cost-effective option for high-volume automation',
        contextWindow: 1048576,
        maxOutputTokens: 65536,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'streaming'],
        tier: 'fast',
        releaseDate: '2026-03',
    },
    'gemini-3-flash-preview': {
        id: 'gemini-3-flash-preview',
        provider: 'google',
        displayName: 'Gemini 3 Flash',
        description: 'Earlier Gemini 3 Flash preview',
        contextWindow: 1048576,
        maxOutputTokens: 65536,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'streaming'],
        isReasoning: true,
        thinking: 'adaptive',
        tier: 'standard',
        releaseDate: '2025-12',
    },

    // ── Gemini 2.5 (GA-stable until 2026-10-16) ───────────────────────────────
    'gemini-2.5-pro': {
        id: 'gemini-2.5-pro',
        provider: 'google',
        displayName: 'Gemini 2.5 Pro',
        description: 'Previous-generation thinking model — GA until 2026-10-16',
        contextWindow: 1048576,
        maxOutputTokens: 65536,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'streaming'],
        isReasoning: true,
        thinking: 'adaptive',
        tier: 'standard',
        releaseDate: '2025-06',
    },
    'gemini-2.5-flash': {
        id: 'gemini-2.5-flash',
        provider: 'google',
        displayName: 'Gemini 2.5 Flash',
        description: 'Previous-generation fast model — GA until 2026-10-16',
        contextWindow: 1048576,
        maxOutputTokens: 65536,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'streaming'],
        isReasoning: true,
        thinking: 'adaptive',
        tier: 'standard',
        releaseDate: '2025-06',
    },
    'gemini-2.5-flash-lite': {
        id: 'gemini-2.5-flash-lite',
        provider: 'google',
        displayName: 'Gemini 2.5 Flash-Lite',
        description: 'Previous-generation lightweight model — GA until 2026-10-16',
        contextWindow: 1048576,
        maxOutputTokens: 65536,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'streaming'],
        isReasoning: true,
        thinking: 'adaptive',
        tier: 'fast',
        releaseDate: '2025-07',
    },

    // ── Shut down by Google (hidden, kept resolvable) ─────────────────────────
    'gemini-3-pro-preview': {
        id: 'gemini-3-pro-preview',
        provider: 'google',
        displayName: 'Gemini 3 Pro (preview)',
        description: 'Shut down by Google — use Gemini 3.1 Pro',
        contextWindow: 1048576,
        maxOutputTokens: 65536,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-11',
    },
    'gemini-2.0-flash': {
        id: 'gemini-2.0-flash',
        provider: 'google',
        displayName: 'Gemini 2.0 Flash',
        description: 'Shut down by Google — use Gemini 3.6 Flash',
        contextWindow: 1048576,
        maxOutputTokens: 8192,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-02',
    },
    'gemini-2.0-flash-lite': {
        id: 'gemini-2.0-flash-lite',
        provider: 'google',
        displayName: 'Gemini 2.0 Flash-Lite',
        description: 'Shut down by Google — use Gemini 3.5 Flash-Lite',
        contextWindow: 1048576,
        maxOutputTokens: 8192,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-02',
    },
    'gemini-1.5-pro': {
        id: 'gemini-1.5-pro',
        provider: 'google',
        displayName: 'Gemini 1.5 Pro',
        description: 'Shut down by Google — use Gemini 3.1 Pro',
        contextWindow: 2000000,
        maxOutputTokens: 8192,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2024-02',
    },
    'gemini-1.5-flash': {
        id: 'gemini-1.5-flash',
        provider: 'google',
        displayName: 'Gemini 1.5 Flash',
        description: 'Shut down by Google — use Gemini 3.6 Flash',
        contextWindow: 1000000,
        maxOutputTokens: 8192,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2024-05',
    },

    // =========================================================================
    // XAI - Grok Models
    // Note: xAI API requires '-latest' suffix for model IDs
    // =========================================================================

    // ── Grok 4.5 (current flagship) ───────────────────────────────────────────
    'grok-4.5': {
        id: 'grok-4.5',
        provider: 'xai',
        displayName: 'Grok 4.5',
        description: 'Flagship Grok: agentic tool calling with configurable reasoning',
        contextWindow: 500000,
        maxOutputTokens: 32768,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'streaming'],
        isReasoning: true,
        thinking: 'effort',
        defaultEffort: 'high',
        tier: 'flagship',
        releaseDate: '2026-06',
    },

    // ── Retired by xAI on 2026-05-15 (hidden, kept resolvable) ────────────────
    'grok-4-1-fast-reasoning-latest': {
        id: 'grok-4-1-fast-reasoning-latest',
        provider: 'xai',
        displayName: 'Grok 4.1 Fast (Reasoning)',
        description: 'Retired 2026-05-15 — use Grok 4.5',
        contextWindow: 2000000,
        maxOutputTokens: 32768,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-11',
    },
    'grok-4-1-fast-non-reasoning-latest': {
        id: 'grok-4-1-fast-non-reasoning-latest',
        provider: 'xai',
        displayName: 'Grok 4.1 Fast',
        description: 'Retired 2026-05-15 — use Grok 4.5',
        contextWindow: 2000000,
        maxOutputTokens: 32768,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-11',
    },
    'grok-4-latest': {
        id: 'grok-4-latest',
        provider: 'xai',
        displayName: 'Grok 4',
        description: 'Retired 2026-05-15 — use Grok 4.5',
        contextWindow: 131072,
        maxOutputTokens: 16384,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-08',
    },
    'grok-4-fast-latest': {
        id: 'grok-4-fast-latest',
        provider: 'xai',
        displayName: 'Grok 4 Fast',
        description: 'Retired 2026-05-15 — use Grok 4.5',
        contextWindow: 131072,
        maxOutputTokens: 16384,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-08',
    },
    'grok-3-latest': {
        id: 'grok-3-latest',
        provider: 'xai',
        displayName: 'Grok 3',
        description: 'Retired 2026-05-15 — use Grok 4.5',
        contextWindow: 131072,
        maxOutputTokens: 16384,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-02',
    },
    'grok-3-fast-latest': {
        id: 'grok-3-fast-latest',
        provider: 'xai',
        displayName: 'Grok 3 Fast',
        description: 'Retired 2026-05-15 — use Grok 4.5',
        contextWindow: 131072,
        maxOutputTokens: 16384,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-02',
    },
    'grok-3-mini-latest': {
        id: 'grok-3-mini-latest',
        provider: 'xai',
        displayName: 'Grok 3 Mini',
        description: 'Retired 2026-05-15 — use Grok 4.5',
        contextWindow: 131072,
        maxOutputTokens: 16384,
        capabilities: ['chat', 'reasoning', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-02',
    },
    'grok-3-mini-fast-latest': {
        id: 'grok-3-mini-fast-latest',
        provider: 'xai',
        displayName: 'Grok 3 Mini Fast',
        description: 'Retired 2026-05-15 — use Grok 4.5',
        contextWindow: 131072,
        maxOutputTokens: 16384,
        capabilities: ['chat', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-02',
    },
    'grok-2-latest': {
        id: 'grok-2-latest',
        provider: 'xai',
        displayName: 'Grok 2',
        description: 'Retired 2026-05-15 — use Grok 4.5',
        contextWindow: 131072,
        maxOutputTokens: 8192,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2024-08',
    },
    'grok-2-vision-latest': {
        id: 'grok-2-vision-latest',
        provider: 'xai',
        displayName: 'Grok 2 Vision',
        description: 'Retired 2026-05-15 — use Grok 4.5',
        contextWindow: 131072,
        maxOutputTokens: 8192,
        capabilities: ['chat', 'vision', 'code', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2024-08',
    },

    // =========================================================================
    // DEEPSEEK - DeepSeek Models
    // =========================================================================

    // ── DeepSeek V4 (current) ─────────────────────────────────────────────────
    // Both support non-thinking and thinking (default) modes. 1M context.
    'deepseek-v4-pro': {
        id: 'deepseek-v4-pro',
        provider: 'deepseek',
        displayName: 'DeepSeek V4 Pro',
        description: 'Most capable DeepSeek with 1M context and thinking mode',
        contextWindow: 1000000,
        maxOutputTokens: 384000,
        capabilities: ['chat', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        thinking: 'effort',
        tier: 'flagship',
        releaseDate: '2026-07',
    },
    'deepseek-v4-flash': {
        id: 'deepseek-v4-flash',
        provider: 'deepseek',
        displayName: 'DeepSeek V4 Flash',
        description: 'Fast, very low-cost DeepSeek with 1M context and thinking mode',
        contextWindow: 1000000,
        maxOutputTokens: 384000,
        capabilities: ['chat', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        thinking: 'effort',
        tier: 'fast',
        releaseDate: '2026-07',
    },

    // ── Superseded DeepSeek aliases (hidden, kept resolvable) ─────────────────
    'deepseek-chat': {
        id: 'deepseek-chat',
        provider: 'deepseek',
        displayName: 'DeepSeek Chat (V3.2)',
        description: 'Superseded — use DeepSeek V4 Flash',
        contextWindow: 128000,
        maxOutputTokens: 8192,
        capabilities: ['chat', 'code', 'function_calling', 'json_mode', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-12',
    },
    'deepseek-reasoner': {
        id: 'deepseek-reasoner',
        provider: 'deepseek',
        displayName: 'DeepSeek Reasoner (V3.2)',
        description: 'Superseded — use DeepSeek V4 Pro',
        contextWindow: 128000,
        maxOutputTokens: 64000,
        capabilities: ['chat', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-12',
    },

    // =========================================================================
    // Z.AI / ZHIPU - GLM Models
    // Context and output limits verified from docs.z.ai model pages.
    // =========================================================================

    'glm-5.2': {
        id: 'glm-5.2',
        provider: 'glm',
        displayName: 'GLM-5.2',
        description: 'Flagship GLM for long-horizon agentic tasks with 1M context',
        contextWindow: 1000000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        thinking: 'effort',
        tier: 'flagship',
        releaseDate: '2026-07',
    },
    'glm-5.1': {
        id: 'glm-5.1',
        provider: 'glm',
        displayName: 'GLM-5.1',
        description: 'High-capability GLM with thinking mode and 200K context',
        contextWindow: 200000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        thinking: 'effort',
        tier: 'flagship',
        releaseDate: '2026-05',
    },
    'glm-5': {
        id: 'glm-5',
        provider: 'glm',
        displayName: 'GLM-5',
        description: 'Balanced GLM-5 generation model with thinking mode',
        contextWindow: 200000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        thinking: 'effort',
        tier: 'standard',
        releaseDate: '2026-04',
    },
    'glm-5-turbo': {
        id: 'glm-5-turbo',
        provider: 'glm',
        displayName: 'GLM-5 Turbo',
        description: 'Faster GLM-5 variant tuned for throughput',
        contextWindow: 200000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        thinking: 'effort',
        tier: 'fast',
        releaseDate: '2026-04',
    },
    'glm-4.7': {
        id: 'glm-4.7',
        provider: 'glm',
        displayName: 'GLM-4.7',
        description: 'Cost-effective GLM with thinking mode and 200K context',
        contextWindow: 200000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        thinking: 'effort',
        tier: 'standard',
        releaseDate: '2026-02',
    },
    'glm-4.7-flash': {
        id: 'glm-4.7-flash',
        provider: 'glm',
        displayName: 'GLM-4.7 Flash',
        description: 'Free, fast GLM tier for lightweight tasks',
        contextWindow: 200000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'code', 'function_calling', 'json_mode', 'streaming'],
        tier: 'fast',
        releaseDate: '2026-02',
    },
    'glm-4.6': {
        id: 'glm-4.6',
        provider: 'glm',
        displayName: 'GLM-4.6',
        description: 'Strong coding and agentic model with 200K context',
        contextWindow: 200000,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'code', 'function_calling', 'json_mode', 'streaming'],
        tier: 'standard',
        releaseDate: '2025-09',
    },
    'glm-4.5': {
        id: 'glm-4.5',
        provider: 'glm',
        displayName: 'GLM-4.5',
        description: 'Previous-generation GLM with 128K context',
        contextWindow: 128000,
        maxOutputTokens: 96000,
        capabilities: ['chat', 'code', 'function_calling', 'json_mode', 'streaming'],
        tier: 'standard',
        releaseDate: '2025-07',
    },
    'glm-4.5-air': {
        id: 'glm-4.5-air',
        provider: 'glm',
        displayName: 'GLM-4.5 Air',
        description: 'Lightweight, very low-cost GLM-4.5 variant',
        contextWindow: 128000,
        maxOutputTokens: 96000,
        capabilities: ['chat', 'code', 'function_calling', 'json_mode', 'streaming'],
        tier: 'fast',
        releaseDate: '2025-07',
    },

    // =========================================================================
    // MOONSHOT - Kimi Models
    // IDs and context windows verified from platform.kimi.ai/docs/models.md
    // =========================================================================

    'kimi-k3': {
        id: 'kimi-k3',
        provider: 'kimi',
        displayName: 'Kimi K3',
        description: 'Flagship Kimi: native vision, 1M context, always reasoning',
        contextWindow: 1048576,
        maxOutputTokens: 128000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        // Always reasons; depth set via the top-level `reasoning_effort` field
        // (low | high | max, default max).
        thinking: 'effort',
        defaultEffort: 'high',
        tier: 'flagship',
        releaseDate: '2026-06',
    },
    'kimi-k2.7-code': {
        id: 'kimi-k2.7-code',
        provider: 'kimi',
        displayName: 'Kimi K2.7 Code',
        description: 'Dedicated coding model with reliable long-context instruction following',
        contextWindow: 256000,
        maxOutputTokens: 64000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'streaming'],
        isReasoning: true,
        thinking: 'effort',
        tier: 'flagship',
        releaseDate: '2026-04',
    },
    'kimi-k2.7-code-highspeed': {
        id: 'kimi-k2.7-code-highspeed',
        provider: 'kimi',
        displayName: 'Kimi K2.7 Code (High-Speed)',
        description: 'High-throughput K2.7 Code, ~180 tokens/s output',
        contextWindow: 256000,
        maxOutputTokens: 64000,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'json_mode', 'streaming'],
        tier: 'fast',
        releaseDate: '2026-04',
    },
    'kimi-k2.6': {
        id: 'kimi-k2.6',
        provider: 'kimi',
        displayName: 'Kimi K2.6',
        description: 'Vision + text with thinking and non-thinking modes',
        contextWindow: 256000,
        maxOutputTokens: 64000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'streaming'],
        isReasoning: true,
        thinking: 'effort',
        tier: 'standard',
        releaseDate: '2026-02',
    },
    'kimi-k2.5': {
        id: 'kimi-k2.5',
        provider: 'kimi',
        displayName: 'Kimi K2.5',
        description: 'Sunsetting 2026-08-31 — use Kimi K3',
        contextWindow: 256000,
        maxOutputTokens: 64000,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2025-11',
    },
    'moonshot-v1-128k': {
        id: 'moonshot-v1-128k',
        provider: 'kimi',
        displayName: 'Moonshot v1 128K',
        description: 'Sunsetting 2026-08-31 — use Kimi K3',
        contextWindow: 128000,
        maxOutputTokens: 8192,
        capabilities: ['chat', 'code', 'function_calling', 'streaming'],
        isDeprecated: true,
        tier: 'legacy',
        releaseDate: '2024-01',
    },

    // =========================================================================
    // ALIBABA - Qwen Models (Model Studio / DashScope)
    // Model IDs verified from the Model Studio docs. NOTE: the spec table on
    // alibabacloud.com is client-rendered, so the context/output figures below
    // are conservative defaults rather than doc-sourced values — they only
    // affect history trimming and the picker's metadata line, not routing.
    // =========================================================================

    'qwen3.7-max': {
        id: 'qwen3.7-max',
        provider: 'qwen',
        displayName: 'Qwen3.7 Max',
        description: 'Flagship Qwen for complex reasoning and agentic work',
        contextWindow: 262144,
        maxOutputTokens: 32768,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        thinking: 'effort',
        tier: 'flagship',
        releaseDate: '2026-06',
    },
    'qwen3.7-plus': {
        id: 'qwen3.7-plus',
        provider: 'qwen',
        displayName: 'Qwen3.7 Plus',
        description: 'Balanced Qwen with long context and thinking mode',
        contextWindow: 1000000,
        maxOutputTokens: 32768,
        capabilities: ['chat', 'vision', 'reasoning', 'code', 'function_calling', 'json_mode', 'extended_thinking', 'streaming'],
        isReasoning: true,
        thinking: 'effort',
        tier: 'standard',
        releaseDate: '2026-06',
    },
    'qwen3.6-flash': {
        id: 'qwen3.6-flash',
        provider: 'qwen',
        displayName: 'Qwen3.6 Flash',
        description: 'Fast, low-cost Qwen for high-volume tasks',
        contextWindow: 1000000,
        maxOutputTokens: 32768,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'json_mode', 'streaming'],
        tier: 'fast',
        releaseDate: '2026-05',
    },
    'qwen3.5-omni-plus': {
        id: 'qwen3.5-omni-plus',
        provider: 'qwen',
        displayName: 'Qwen3.5 Omni Plus',
        description: 'Omni-modal Qwen handling text, image and audio input',
        contextWindow: 262144,
        maxOutputTokens: 32768,
        capabilities: ['chat', 'vision', 'code', 'function_calling', 'streaming'],
        tier: 'standard',
        releaseDate: '2026-03',
    },
} as const;

// =============================================================================
// BACKWARDS COMPATIBILITY - Legacy exports
// =============================================================================

// Create the old chatModels format from MODEL_REGISTRY
export const chatModels = Object.fromEntries(
    Object.entries(MODEL_REGISTRY).map(([key, config]) => [
        key,
        { id: config.id, type: config.provider }
    ])
) as Record<string, { id: string; type?: AIProvider }>;

// List of all model IDs
export const chatModelsList = Object.keys(MODEL_REGISTRY) as (keyof typeof MODEL_REGISTRY)[];

// List of all model IDs (same as chatModelsList now - no more dbId mapping)
export const chatModelsListDb = Object.keys(MODEL_REGISTRY) as string[];

// Types
export type ChatModel = keyof typeof MODEL_REGISTRY | string;

// Default model for new sessions/instructions.
// GPT-5.6 Terra balances intelligence and cost — the sensible everyday default.
export const DEFAULT_MODEL: ChatModel = 'gpt-5.6-terra';
export const DEFAULT_MODEL_ID = MODEL_REGISTRY['gpt-5.6-terra'].id;

/** Per-provider default, used when a user only has one provider's key configured. */
export const DEFAULT_MODEL_BY_PROVIDER: Record<AIProvider, ChatModel> = {
    openai: 'gpt-5.6-terra',
    anthropic: 'claude-sonnet-5',
    google: 'gemini-3.6-flash',
    xai: 'grok-4.5',
    deepseek: 'deepseek-v4-flash',
    glm: 'glm-5.2',
    kimi: 'kimi-k3',
    qwen: 'qwen3.7-max',
};

// =============================================================================
// MODEL SELECTION - UI-friendly model lists
// =============================================================================

export type ModelCategory = 'openai' | 'anthropic' | 'google' | 'xai' | 'deepseek' | 'glm' | 'kimi' | 'qwen';

export interface ModelOption {
    label: string;
    value: ChatModel;
    desc: string;
    type: ModelCategory;
    tier: ModelConfig['tier'];
    capabilities: ModelCapability[];
    contextWindow: number;
    maxOutputTokens: number;
    isReasoning: boolean;
    isNew?: boolean;
    isDeprecated?: boolean;
}

/** Provider display order in grouped pickers. */
export const PROVIDER_ORDER: readonly AIProvider[] = ['openai', 'anthropic', 'google', 'xai', 'deepseek', 'glm', 'kimi', 'qwen'];

const TIER_ORDER: Record<ModelConfig['tier'], number> = { flagship: 0, standard: 1, fast: 2, legacy: 3 };

/** A model counts as "new" if released within the last 4 months. */
const NEW_MODEL_CUTOFF_MONTHS = 4;

const isRecentRelease = (releaseDate?: string): boolean => {
    if (!releaseDate) return false;
    const released = new Date(`${releaseDate}-01T00:00:00Z`);
    if (Number.isNaN(released.getTime())) return false;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - NEW_MODEL_CUTOFF_MONTHS);
    return released >= cutoff;
};

// Generate model options from registry
const generateModelOptions = (): ModelOption[] => {
    return Object.entries(MODEL_REGISTRY)
        .filter(([, config]) => !('isDeprecated' in config && config.isDeprecated))
        .map(([key, config]) => ({
            label: config.displayName,
            value: key as ChatModel,
            desc: config.description,
            type: config.provider as ModelCategory,
            tier: config.tier,
            capabilities: [...config.capabilities],
            contextWindow: config.contextWindow,
            maxOutputTokens: config.maxOutputTokens,
            isReasoning: 'isReasoning' in config ? !!config.isReasoning : false,
            isNew: isRecentRelease('releaseDate' in config ? config.releaseDate : undefined),
            isDeprecated: false,
        }))
        .sort((a, b) => {
            // Provider first (keeps grouped pickers stable), then tier, then newest.
            const providerDelta = PROVIDER_ORDER.indexOf(a.type) - PROVIDER_ORDER.indexOf(b.type);
            if (providerDelta !== 0) return providerDelta;
            const tierDelta = TIER_ORDER[a.tier] - TIER_ORDER[b.tier];
            if (tierDelta !== 0) return tierDelta;
            return a.label.localeCompare(b.label);
        });
};

export const allModelOptions = generateModelOptions();

// Backwards compatible export - includes all non-deprecated models from all providers
export const openAiChatModels = allModelOptions;

// Provider-specific model lists
export const openAiModels = allModelOptions.filter(m => m.type === 'openai');
export const anthropicModels = allModelOptions.filter(m => m.type === 'anthropic');
export const googleModels = allModelOptions.filter(m => m.type === 'google');
export const xaiModels = allModelOptions.filter(m => m.type === 'xai');
export const deepseekModels = allModelOptions.filter(m => m.type === 'deepseek');
export const glmModels = allModelOptions.filter(m => m.type === 'glm');
export const kimiModels = allModelOptions.filter(m => m.type === 'kimi');
export const qwenModels = allModelOptions.filter(m => m.type === 'qwen');

// =============================================================================
// HELPER FUNCTIONS - Model capability checks
// =============================================================================

export const getModelConfig = (modelId: string): ModelConfig | undefined => {
    return MODEL_REGISTRY[modelId as keyof typeof MODEL_REGISTRY];
};

export const getModelProvider = (modelId: string): AIProvider | undefined => {
    return getModelConfig(modelId)?.provider;
};

export const isModelAllowedVision = (model?: ChatModel): boolean => {
    if (!model) return false;
    const config = getModelConfig(model);
    return config?.capabilities.includes('vision') ?? false;
};

export const isModelAnthropic = (model: ChatModel): boolean => {
    return getModelProvider(model) === 'anthropic';
};

export const isModelOpenAI = (model: ChatModel): boolean => {
    return getModelProvider(model) === 'openai';
};

export const isModelGoogle = (model: ChatModel): boolean => {
    return getModelProvider(model) === 'google';
};

export const isModelXAI = (model: ChatModel): boolean => {
    return getModelProvider(model) === 'xai';
};

export const isModelDeepSeek = (model: ChatModel): boolean => {
    return getModelProvider(model) === 'deepseek';
};

export const isModelGLM = (model: ChatModel): boolean => {
    return getModelProvider(model) === 'glm';
};

export const isModelKimi = (model: ChatModel): boolean => {
    return getModelProvider(model) === 'kimi';
};

export const isModelQwen = (model: ChatModel): boolean => {
    return getModelProvider(model) === 'qwen';
};

export const isModelReasoning = (model: ChatModel): boolean => {
    const config = getModelConfig(model);
    return config?.isReasoning ?? false;
};

/**
 * Whether it is safe to send sampling parameters (temperature/top_p/top_k).
 *
 * Claude Opus 5 / Sonnet 5 / Fable 5 / Opus 4.7 / 4.8 and the OpenAI reasoning
 * models reject these with a 400, so callers must omit them entirely rather than
 * passing a default. Unknown models default to `true` (permissive) so that a
 * model ID we don't have metadata for still behaves like a plain chat model.
 */
export const supportsSampling = (model: ChatModel): boolean => {
    const config = getModelConfig(model);
    if (!config) return true;
    return config.supportsSampling ?? true;
};

/** How thinking should be requested for this model. */
export const getThinkingMode = (model: ChatModel): ThinkingMode => {
    const config = getModelConfig(model);
    if (!config) return 'none';
    if (config.thinking) return config.thinking;
    // Fall back to legacy budget-style thinking for older reasoning models.
    return config.isReasoning ? 'budget' : 'none';
};

/** Default reasoning effort for this model, if it supports one. */
export const getDefaultEffort = (model: ChatModel): ReasoningEffort | undefined =>
    getModelConfig(model)?.defaultEffort;

/**
 * Clamp an effort level to what @ai-sdk/anthropic accepts (low | medium | high).
 * 'xhigh' exists on the raw Anthropic API but is not in the SDK's zod schema yet,
 * so sending it would fail client-side validation.
 */
export const clampAnthropicEffort = (effort?: ReasoningEffort): 'low' | 'medium' | 'high' | undefined => {
    if (!effort) return undefined;
    return effort === 'xhigh' ? 'high' : effort;
};

// Legacy compatibility - checks for O-series reasoning models
export const isModelO1 = (model: ChatModel): boolean => {
    return model.startsWith('o1') || model.startsWith('o3') || model.startsWith('o4');
};

export const hasCapability = (model: ChatModel, capability: ModelCapability): boolean => {
    const config = getModelConfig(model);
    return config?.capabilities.includes(capability) ?? false;
};

export const getModelsByProvider = (provider: AIProvider): ModelConfig[] => {
    return Object.values(MODEL_REGISTRY).filter(m => m.provider === provider);
};

export const getModelsByTier = (tier: ModelConfig['tier']): ModelConfig[] => {
    return Object.values(MODEL_REGISTRY).filter(m => m.tier === tier);
};

export const getActiveModels = (): ModelConfig[] => {
    return Object.values(MODEL_REGISTRY).filter(m => !('isDeprecated' in m && m.isDeprecated)) as unknown as ModelConfig[];
};

export const getRecommendedModels = (): ModelConfig[] => {
    return Object.values(MODEL_REGISTRY).filter(
        m => !('isDeprecated' in m && m.isDeprecated) && (m.tier === 'flagship' || m.tier === 'fast')
    ) as unknown as ModelConfig[];
};

export const chatInstructionsDefault = [
    {
        name: "Fitness Coach",
        instruction: "You are a fitness coach, offering workout routines, exercise tips, and motivation to help users achieve their fitness goals.",
        short_description: "A fitness coach that offers workout routines and exercise tips",
        color: "#EC7063",
        icon: <FaRunning />
    },
    {
        name: "Personal Finance Advisor",
        instruction: "You are a personal finance advisor, offering budgeting tips, savings strategies, and investment advice to help users manage their money and reach their financial goals.",
        short_description: "A personal finance advisor that helps users manage their money and achieve financial goals",
        color: "#58D68D",
        icon: <FaCommentDollar />
    },
    {
        name: "Growth Hacker",
        instruction: "You are a growth hacker, sharing innovative marketing strategies, user acquisition techniques, and analytics insights to help startups and founders achieve rapid growth and user engagement.",
        short_description: "A growth hacker that shares strategies for rapid growth and user engagement",
        color: "#16A085",
        icon: <FaChartLine />
    },
    {
        name: "Web Development Expert",
        instruction: "You are a web development expert, providing guidance on front-end and back-end technologies, web frameworks, and best practices for building responsive and user-friendly websites.",
        short_description: "A web development expert that offers guidance on building responsive and user-friendly websites",
        color: "#F1948A",
        icon: <FaCode />
    },
    {
        name: "Nutritionist",
        instruction: "You are a nutritionist, providing guidance on healthy eating habits, meal planning, and dietary adjustments based on the user's goals and dietary restrictions.",
        short_description: "A nutritionist that knows healthy lifestyle and diet to help user's goal",
        color: "#ffb106",
        icon: <FaAppleAlt />
    },
    {
        name: "Language Tutor",
        instruction: "You are a language tutor, assisting users with grammar, vocabulary, pronunciation, and conversational skills in their target language.",
        short_description: "A language tutor that helps users improve their skills in their target language",
        color: "#3498DB",
        icon: <FaBook />
    },
    {
        name: "Relationship Counselor",
        instruction: "You are a relationship counselor, offering advice and guidance to help users navigate interpersonal challenges and improve their relationships.",
        short_description: "A relationship counselor that assists users in navigating interpersonal challenges",
        color: "#AF7AC5",
        icon: <FaHeart />
    },
    {
        name: "Time Management Coach",
        instruction: "You are a time management coach, providing strategies for effective time management, organization, and productivity.",
        short_description: "A time management coach that offers strategies for better organization and productivity",
        color: "#F1C40F",
        icon: <FaCalendarAlt />
    },
    {
        name: "Solo Founder Coach",
        instruction: "You are a solo founder coach, offering support and guidance to solo entrepreneurs on managing their time and resources effectively, building a network, and staying motivated while building a business on their own.",
        short_description: "A solo founder coach that offers support and guidance to solo entrepreneurs",
        color: "#7D3C98",
        icon: <FaUserTie />
    },
    {
        name: "Film Critic",
        instruction: "You are a film critic, providing movie recommendations, reviews, and insights into the filmmaking process.",
        short_description: "A film critic that offers movie recommendations and insights into filmmaking",
        color: "#CCCCCC",
        icon: <FaFilm />
    },
    {
        name: "Interior Designer",
        instruction: "You are an interior designer, offering suggestions on home décor, furniture arrangement, and color schemes to improve users' living spaces.",
        short_description: "An interior designer that helps users improve their living spaces",
        color: "#F1948A",
        icon: <FaPaintRoller />
    },
    {
        name: "Parenting Advisor",
        instruction: "You are a parenting advisor, offering guidance on child development, discipline, and family dynamics to help users navigate the challenges of raising children.",
        short_description: "A parenting advisor that offers guidance on child development and family dynamics",
        color: "#73C6B6",
        icon: <FaUsers />
    },
    {
        name: "Book Recommender",
        instruction: "You are a book recommender, suggesting books based on users' interests, favorite genres, and reading preferences.",
        short_description: "A book recommender that suggests books based on user interests and preferences",
        color: "#A569BD",
        icon: <FaBook />
    }, {
        name: "Tech Support Specialist",
        instruction: "You are a tech support specialist, helping users troubleshoot and resolve issues with their computers, smartphones, and other devices.",
        short_description: "A tech support specialist that assists users with troubleshooting and resolving device issues",
        color: "#5DADE2",
        icon: <FaLaptopCode />
    },
    {
        name: "Mental Health Counselor",
        instruction: "You are a mental health counselor, providing support and guidance for users dealing with stress, anxiety, depression, and other mental health challenges.",
        short_description: "A mental health counselor that offers support and guidance for mental health challenges",
        color: "#45B39D",
        icon: <FaUserAstronaut />
    },
    {
        name: "Study Skills Coach",
        instruction: "You are a study skills coach, offering tips and strategies to help users improve their academic performance and study habits.",
        short_description: "A study skills coach that offers tips for improving academic performance and study habits",
        color: "#F0B27A",
        icon: <FaLightbulb />
    },
    {
        name: "Eco-friendly Lifestyle Consultant",
        instruction: "You are an eco-friendly lifestyle consultant, offering advice on sustainable living practices, waste reduction, and environmentally-friendly product choices.",
        short_description: "An eco-friendly lifestyle consultant that offers advice on sustainable living practices",
        color: "#82E0AA",
        icon: <FaLeaf />
    },
    {
        name: "Personal Shopper",
        instruction: "You are a personal shopper, helping users find the perfect outfit, accessories, or gifts based on their preferences and budget.",
        short_description: "A personal shopper that helps users find the perfect items based on their preferences",
        color: "#BB8FCE",
        icon: <FaShoppingBag />
    },
    {
        name: "Event Planner",
        instruction: "You are an event planner, assisting users in planning and organizing parties, weddings, corporate events, and other special occasions.",
        short_description: "An event planner that helps users plan and organize special occasions",
        color: "#E74C3C",
        icon: <FaCalendarPlus />
    },
    {
        name: "Real Estate Consultant",
        instruction: "You are a real estate consultant, offering advice on buying, selling, and renting properties, as well as insights into the real estate market.",
        short_description: "A real estate consultant that offers advice on property transactions and market insights",
        color: "#2E86C1",
        icon: <FaBuilding />
    },
    {
        name: "Historical Expert",
        instruction: "You are a historical expert, providing information and context on historical events, figures, and societies.",
        short_description: "A historical expert that provides information and context on historical events and figures",
        color: "#A93226",
        icon: <FaLandmark />
    },
    {
        name: "Gaming Enthusiast",
        instruction: "You are a gaming enthusiast, offering tips, strategies, and recommendations for video games and gaming hardware.",
        short_description: "A gaming enthusiast that offers tips and recommendations for video games and hardware",
        color: "#F39C12",
        icon: <FaGamepad />
    },
    {
        name: "DIY Home Improvement Guide",
        instruction: "You are a DIY home improvement guide, offering tips and advice on various home improvement projects, from painting and decorating to repairs and renovations.",
        short_description: "A DIY home improvement guide that offers tips and advice on various home projects",
        color: "#76D7C4",
        icon: <FaTools />
    },
    {
        name: "Startup Mentor",
        instruction: "You are a startup mentor, offering guidance and advice to aspiring entrepreneurs and founders on topics such as ideation, market research, business planning, and funding strategies.",
        short_description: "A startup mentor that offers guidance and advice to aspiring entrepreneurs and founders",
        color: "#28B463",
        icon: <FaLightbulb />
    },
    /* {
        name: "Y Combinator Expert",
        instruction: "You are a Y Combinator expert, providing insights into the prestigious accelerator program, including the application process, what to expect during the program, and tips for success in the Y Combinator ecosystem.",
        short_description: "A Y Combinator expert that provides insights into the prestigious accelerator program",
        color: "#E67E22",
        icon: <FaHandshake />
    }, */
    /* {
        name: "Product Strategist",
        instruction: "You are a Product strategist, advising users on how to successfully launch their product on Product Hunt, Social Media, Hackernews & reddit, including creating an engaging product presentation, building a community, and promoting the launch.",
        short_description: "A Product strategist that advises users on successful product launches on the platform",
        color: "#D35400",
        icon: <FaRocket />
    }, */
    {
        name: "Mobile App Developer",
        instruction: "You are a mobile app developer, offering insights into developing apps for iOS and Android platforms, discussing programming languages, app design principles, and app store optimization.",
        short_description: "A mobile app developer that offers insights into developing apps for iOS and Android platforms",
        color: "#5499C7",
        icon: <FaMobileAlt />
    },
    {
        name: "Data Science Advisor",
        instruction: "You are a data science advisor, helping users navigate the world of data analysis, machine learning, and artificial intelligence by offering advice on tools, libraries, and algorithms.",
        short_description: "A data science advisor that offers advice on tools and techniques for data analysis and machine learning",
        color: "#1ABC9C",
        icon: <FaDatabase />
    },
    {
        name: "DevOps Consultant",
        instruction: "You are a DevOps consultant, sharing best practices for software development and deployment, including continuous integration, automated testing, and infrastructure management.",
        short_description: "A DevOps consultant that shares best practices for software development and deployment",
        color: "#C0392B",
        icon: <FaCogs />
    },
    {
        name: "Cybersecurity Expert",
        instruction: "You are a cybersecurity expert, providing tips and guidance on securing software applications, protecting sensitive data, and staying up-to-date on the latest cybersecurity trends and threats.",
        short_description: "A cybersecurity expert that provides tips and guidance on securing software applications and protecting sensitive data",
        color: "#34495E",
        icon: <FaShieldAlt />
    }
]

export const chatPromptsDefault = [
    {
        "name": "Summarize the paragraph",
        "prompt": "Summarize the following paragraph into [Number] of bullet points: [Paragraph]",
    },
    {
        "name": "Write a Welcome Email",
        "prompt": "Write a welcome email for [Product Name] which does the following [Description of Product] with the following benefits [Benefits of Product] and has the following[Call to Action to for users]"
    },
    {
        "name": "Landing Page Headline",
        "prompt": "Write a landing page headline for [What is the product] with the following benefits: [Benefit 1], [Benefit 2], [Benefit 3]",
    },
    {
        "name": "Senior Frontend Developer",
        "prompt": "I want you to act as a Senior Frontend developer. I will describe a project details you will code project with this tools: Create React App, yarn, Ant Design, List, Redux Toolkit, createSlice, thunk, axios. You should merge files in single index.js file and nothing else. Do not write explanations. My first request is [[Create ....]]",
    },
    {
        "name": "Motivational Coach",
        "prompt": "I want you to act as a motivational coach. I will provide you with some information about someone's goals and challenges, and it will be your job to come up with strategies that can help this person achieve their goals. This could involve providing positive affirmations, giving helpful advice or suggesting activities they can do to reach their end goal. My first request is [[I need help motivating myself to stay disciplined while studying for an upcoming exam]]."
    }
]
